import type { QueryClient } from '@tanstack/react-query';
import { createBrowserRouter, Navigate } from 'react-router';
import { onUnauthorized } from '../lib/api.js';
import { qk } from '../lib/query.js';
import { BusinessPickerPage, HomeRedirect } from '../pages/business-picker.js';
import { NotFoundPage, RouteErrorPage } from '../pages/not-found.js';
import { SignInPage } from '../pages/sign-in.js';
import { RequireSession } from './require-session.js';
import { TenantLayout } from './tenant-layout.js';

const connections = () => import('../pages/connections/connections-page.js').then((m) => ({ Component: m.ConnectionsPage }));

export function createAppRouter(queryClient: QueryClient) {
  const router = createBrowserRouter([
    { path: '/iniciar-sesion', element: <SignInPage />, errorElement: <RouteErrorPage /> },
    // Public self-service pairing for salespeople (token in the URL fragment; no session).
    { path: '/vincular', lazy: () => import('../pages/pairing-page.js').then((m) => ({ Component: m.PairingPage })), errorElement: <RouteErrorPage /> },
    {
      element: <RequireSession />,
      errorElement: <RouteErrorPage />,
      children: [
        { path: '/', element: <HomeRedirect /> },
        { path: '/negocios', element: <BusinessPickerPage /> },
        { path: '/admin/uso-ia', lazy: () => import('../pages/admin/ai-usage-page.js').then((m) => ({ Component: m.AiUsagePage })) },
        {
          path: '/t/:tenantId',
          element: <TenantLayout />,
          children: [
            { index: true, element: <Navigate to="resumen" replace /> },
            // Route-level code splitting keeps the initial bundle small (charts load with report pages).
            { path: 'resumen', lazy: () => import('../pages/overview/overview-page.js').then((m) => ({ Component: m.OverviewPage })) },
            { path: 'alertas', lazy: () => import('../pages/alerts/alerts-page.js').then((m) => ({ Component: m.AlertsPage })) },
            { path: 'conexiones', lazy: connections },
            { path: 'conexiones/:connectionId', lazy: connections },
            { path: 'conversaciones', lazy: () => import('../pages/conversations/conversations-page.js').then((m) => ({ Component: m.ConversationsPage })) },
            {
              path: 'conversaciones/:conversationId',
              lazy: () => import('../pages/conversations/conversation-detail-page.js').then((m) => ({ Component: m.ConversationDetailPage })),
            },
            { path: 'asesores', lazy: () => import('../pages/advisors/advisors-page.js').then((m) => ({ Component: m.AdvisorsPage })) },
            { path: 'desempeno', lazy: () => import('../pages/performance/performance-page.js').then((m) => ({ Component: m.PerformancePage })) },
            { path: 'configuracion', lazy: () => import('../pages/settings/settings-page.js').then((m) => ({ Component: m.SettingsPage })) },
            { path: '*', element: <NotFoundPage /> },
          ],
        },
      ],
    },
    { path: '*', element: <NotFoundPage /> },
  ]);

  // Any 401 from the API: drop cached data and go to sign-in (keeping where the user was).
  let redirecting = false;
  onUnauthorized(() => {
    if (redirecting || router.state.location.pathname === '/iniciar-sesion') return;
    redirecting = true;
    const next = router.state.location.pathname + router.state.location.search;
    // Only say "session expired" when this tab actually had a signed-in user; a first visit is not an expiry.
    const hadSession = queryClient.getQueryData(qk.me) !== undefined;
    queryClient.clear();
    const expired = hadSession ? '&expirada=1' : '';
    void router.navigate(`/iniciar-sesion?siguiente=${encodeURIComponent(next)}${expired}`, { replace: true }).finally(() => {
      redirecting = false;
    });
  });
  return router;
}
